/**
 * Sensitivity Pareto — which tolerance drives the output.
 *
 * Bar length encodes the variance share, so every bar is the same colour
 * (categorical slot 1): re-encoding length as hue would spend the free channel
 * on information already shown. The SIGN of the sensitivity is real
 * engineering information, so it is carried in the numeric label rather than in
 * colour, which keeps the sign readable in greyscale and under CVD.
 *
 * When the linear model explains little of the variance, the SRC column is not
 * trustworthy — the component says so and points at the rank column instead of
 * quietly showing a misleading ranking.
 */

import { useId } from 'react';
import type { OutputResult } from '../../engine/montecarlo.ts';
import { percent, sig } from '../format.ts';
import { Info } from '../Info.tsx';

interface Props {
  result: OutputResult;
  maxRows?: number;
}

export function Pareto({ result, maxRows = 12 }: Props) {
  const titleId = useId();
  const trustSrc = result.regressionR2 >= 0.7;

  // Below a trustworthy R^2 the linear coefficients mis-rank; rank by the
  // monotonic measure instead, which survives nonlinearity.
  const rows = [...result.sensitivities]
    .filter((s) => s.contribution > 0 || Math.abs(s.spearman) > 0.01)
    .sort((a, b) =>
      trustSrc
        ? b.contribution - a.contribution
        : Math.abs(b.spearman) - Math.abs(a.spearman),
    )
    .slice(0, maxRows);

  if (rows.length === 0) {
    return <p className="muted">No variable measurably affects this output.</p>;
  }

  const scale = trustSrc
    ? Math.max(...rows.map((r) => r.contribution))
    : Math.max(...rows.map((r) => Math.abs(r.spearman)));

  return (
    <figure className="chart">
      <figcaption id={titleId} className="chart-title">
        <span className="heading-text">Contribution to {result.id} <Info topic="sensitivity" /></span>
      </figcaption>

      {!trustSrc && (
        <p className="callout warn">
          Linear regression explains only {percent(result.regressionR2, 0)} of the variance,
          so the standardized coefficients understate interactions. Ranked by rank
          correlation instead.
        </p>
      )}

      <div className="pareto" role="table" aria-labelledby={titleId}>
        <div className="pareto-head" role="row">
          <span role="columnheader">Variable</span>
          <span role="columnheader">{trustSrc ? 'Variance share' : 'Rank correlation'}</span>
          <span role="columnheader">SRC</span>
          <span role="columnheader">Spearman</span>
        </div>

        {rows.map((s) => {
          const magnitude = trustSrc ? s.contribution : Math.abs(s.spearman);
          return (
            <div className="pareto-row" role="row" key={s.varId}>
              <span className="pareto-name" role="cell">
                {s.varId}
              </span>
              <span className="pareto-bar-cell" role="cell">
                <span
                  className="pareto-bar"
                  style={{ width: `${(magnitude / scale) * 100}%` }}
                  aria-hidden="true"
                />
                <span className="pareto-value">
                  {trustSrc ? percent(s.contribution, 1) : sig(Math.abs(s.spearman), 3)}
                </span>
              </span>
              <span className="pareto-num" role="cell">
                {s.src >= 0 ? '+' : '−'}
                {sig(Math.abs(s.src), 3)}
              </span>
              <span className="pareto-num" role="cell">
                {s.spearman >= 0 ? '+' : '−'}
                {sig(Math.abs(s.spearman), 3)}
              </span>
            </div>
          );
        })}
      </div>

      <p className="chart-note">
        A positive coefficient means the output rises with the variable.
        {trustSrc && ` Linear model R² = ${sig(result.regressionR2, 3)}.`}
      </p>
    </figure>
  );
}
